"use client"

import { useMemo } from "react"
import { PieChart, Pie, Cell, Label } from "recharts"
import { IconReceiptOff } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"

import type { InvoiceStats } from "@/lib/invoices-api"

// Static module-scope colors (no interpolation). Covers both the dashboard
// contract keys AND the wider backend InvoiceStatus vocabulary ('sent',
// 'partial') so a clinic on the post-Phase-64 enum still gets real colors.
const STATUS_COLOR: Record<string, string> = {
  draft: "var(--chart-3)",
  issued: "var(--chart-1)",
  sent: "var(--chart-1)",
  partial: "var(--chart-4)",
  paid: "var(--chart-2)",
  void: "var(--muted-foreground)",
}

interface InvoiceStatusChartProps {
  data: InvoiceStats | null
  isLoading: boolean
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * Invoice mix by status as a donut — the finance counterpart to
 * {@link import('./leads-status-chart').LeadsStatusChart}, same Apollo panel /
 * Recharts idiom (inner label = total invoice count, legend chips underneath).
 *
 * PRESENTATIONAL ONLY — the caller owns the `getInvoiceStats()` fetch and the
 * `invoices.view` gate. Iterates `by_status` with Object.entries rather than
 * hardcoding keys, so the widget survives the draft|issued vs draft|sent|partial
 * vocabulary drift between clinics.
 */
export function InvoiceStatusChart({ data, isLoading, className }: InvoiceStatusChartProps) {
  const t = useTranslations("dashboard")

  // Static-literal resolver — never t(statusVar) per T-72-01.
  const statusLabel = (key: string): string => {
    switch (key) {
      case "draft":
        return t("charts.invoiceStatus.status.draft")
      case "issued":
        return t("charts.invoiceStatus.status.issued")
      case "sent":
        return t("charts.invoiceStatus.status.sent")
      case "partial":
        return t("charts.invoiceStatus.status.partial")
      case "paid":
        return t("charts.invoiceStatus.status.paid")
      case "void":
        return t("charts.invoiceStatus.status.void")
      default:
        return key
    }
  }

  const chartData = useMemo(() => {
    if (!data?.by_status) return []
    return Object.entries(data.by_status)
      .map(([name, bucket]) => ({
        name,
        value: bucket?.count ?? 0,
        total: bucket?.total ?? 0,
        fill: STATUS_COLOR[name] ?? "var(--chart-5)",
      }))
      .filter((item) => item.value > 0)
  }, [data])

  const chartConfig = useMemo(() => {
    return chartData.reduce((acc, item) => {
      acc[item.name] = { label: statusLabel(item.name), color: item.fill }
      return acc
    }, {} as ChartConfig)
    // statusLabel closes over `t`, which is stable per-locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, t])

  const totalCount = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData],
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("charts.invoiceStatus.title")} className={className}>
        <div className="flex h-[200px] items-center justify-center">
          <Skeleton className="h-[160px] w-[160px] rounded-full" />
        </div>
        <div className="mt-2 flex justify-center gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <ApolloPanel eyebrow={t("charts.invoiceStatus.title")} className={className}>
        <div className="flex h-[200px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconReceiptOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("charts.invoiceStatus.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("charts.invoiceStatus.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("charts.invoiceStatus.title")} className={className}>
      <ChartContainer config={chartConfig} className="mx-auto aspect-square h-[200px]">
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name) => (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: STATUS_COLOR[name as string] }}
                    />
                    <span className="text-muted-foreground">
                      {statusLabel(name as string)}
                    </span>
                    <span className="ml-auto font-medium">{value}</span>
                  </div>
                )}
              />
            }
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={50}
            outerRadius={80}
            strokeWidth={2}
            stroke="hsl(var(--background))"
          >
            {chartData.map((entry) => (
              <Cell key={`cell-${entry.name}`} fill={entry.fill} />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground font-serif text-[26px] leading-none tracking-[-0.01em] tabular-nums"
                      >
                        {totalCount.toLocaleString()}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 20}
                        className="fill-muted-foreground text-xs"
                      >
                        {t("charts.invoiceStatus.totalLabel")}
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className="mt-2 flex flex-wrap justify-center gap-3">
        {chartData.map((entry) => (
          <div key={entry.name} className="flex items-center gap-1.5 text-xs">
            <div
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: entry.fill }}
            />
            <span className="text-muted-foreground">{statusLabel(entry.name)}</span>
            <span className="font-medium tabular-nums">{entry.value}</span>
          </div>
        ))}
      </div>
    </ApolloPanel>
  )
}
